import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Email Service for sending announcements
 * This service handles email functionality for the announcement system
 */

// Email service configuration
const EMAIL_SERVICE_CONFIG = {
  // For development, we'll use a mock service
  // In production, this would integrate with a real email service like SendGrid, AWS SES, etc.
  mockMode: true,
  apiEndpoint: process.env.REACT_APP_EMAIL_API_ENDPOINT || '/api/send-email',
  fromEmail: 'noreply@profstudy.com',
  fromName: 'ProfStudy Admin'
};

/**
 * Get user emails based on target audience
 */
export const getUserEmailsByAudience = async (targetAudience) => {
  try {
    const usersRef = collection(db, 'users');
    let q;

    switch (targetAudience) {
      case 'all':
        // Get all users with email addresses
        q = query(usersRef, where('email', '!=', null));
        break;
      case 'students':
        // Get users with student role
        q = query(usersRef, where('role', '==', 'student'));
        break;
      case 'authors':
        // Get users with author role
        q = query(usersRef, where('role', '==', 'author'));
        break;
      case 'admins':
        // Get users with admin role
        q = query(usersRef, where('role', '==', 'admin'));
        break;
      default:
        return [];
    }

    const querySnapshot = await getDocs(q);
    const emails = [];
    
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      if (userData.email) {
        emails.push({
          email: userData.email,
          name: userData.displayName || userData.name || 'User',
          uid: doc.id
        });
      }
    });

    return emails;
  } catch (error) {
    console.error('Error getting user emails:', error);
    throw error;
  }
};

/**
 * Get additional email addresses from general settings
 */
export const getAdditionalEmails = async () => {
  try {
    const generalSettingsRef = doc(db, 'generalSettings', 'main');
    const generalSettingsDoc = await getDoc(generalSettingsRef);
    
    if (generalSettingsDoc.exists()) {
      const data = generalSettingsDoc.data();
      return data.emails || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error getting additional emails:', error);
    return [];
  }
};

/**
 * Generate email content for announcement
 */
export const generateEmailContent = (announcement) => {
  const priorityColors = {
    low: '#10B981',
    medium: '#3B82F6',
    high: '#F59E0B',
    critical: '#EF4444'
  };

  const priorityLabels = {
    low: 'Low Priority',
    medium: 'Medium Priority',
    high: 'High Priority',
    critical: 'Critical'
  };

  const priorityColor = priorityColors[announcement.priority] || priorityColors.medium;
  const priorityLabel = priorityLabels[announcement.priority] || priorityLabels.medium;

  const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${announcement.title}</title>
        <style>
            body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                line-height: 1.6;
                color: #333;
                max-width: 600px;
                margin: 0 auto;
                padding: 20px;
                background-color: #f8fafc;
            }
            .container {
                background: white;
                border-radius: 12px;
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                overflow: hidden;
            }
            .header {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                padding: 30px;
                text-align: center;
            }
            .header h1 {
                margin: 0;
                font-size: 24px;
                font-weight: 600;
            }
            .priority-badge {
                display: inline-block;
                padding: 6px 12px;
                border-radius: 20px;
                font-size: 12px;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-top: 10px;
                background-color: ${priorityColor};
                color: white;
            }
            .content {
                padding: 30px;
            }
            .announcement-title {
                font-size: 20px;
                font-weight: 600;
                color: #1f2937;
                margin-bottom: 15px;
            }
            .announcement-content {
                font-size: 16px;
                color: #4b5563;
                line-height: 1.7;
                margin-bottom: 20px;
            }
            .metadata {
                background: #f8fafc;
                padding: 20px;
                border-radius: 8px;
                border-left: 4px solid ${priorityColor};
            }
            .metadata-item {
                display: flex;
                justify-content: space-between;
                margin-bottom: 8px;
                font-size: 14px;
            }
            .metadata-label {
                font-weight: 600;
                color: #6b7280;
            }
            .metadata-value {
                color: #374151;
            }
            .footer {
                background: #f8fafc;
                padding: 20px 30px;
                text-align: center;
                border-top: 1px solid #e5e7eb;
            }
            .footer p {
                margin: 0;
                font-size: 14px;
                color: #6b7280;
            }
            .expiry-notice {
                background: #fef3c7;
                border: 1px solid #f59e0b;
                border-radius: 8px;
                padding: 15px;
                margin-top: 20px;
            }
            .expiry-notice p {
                margin: 0;
                color: #92400e;
                font-size: 14px;
            }
        </style>
    </head>
    <body>
        <div class="container">
            <div class="header">
                <h1>ProfStudy Announcement</h1>
                <div class="priority-badge">${priorityLabel}</div>
            </div>
            
            <div class="content">
                <h2 class="announcement-title">${announcement.title}</h2>
                <div class="announcement-content">${announcement.content.replace(/\n/g, '<br>')}</div>
                
                <div class="metadata">
                    <div class="metadata-item">
                        <span class="metadata-label">Priority:</span>
                        <span class="metadata-value">${priorityLabel}</span>
                    </div>
                    <div class="metadata-item">
                        <span class="metadata-label">Target Audience:</span>
                        <span class="metadata-value">${announcement.targetAudience.charAt(0).toUpperCase() + announcement.targetAudience.slice(1)}</span>
                    </div>
                    <div class="metadata-item">
                        <span class="metadata-label">Created:</span>
                        <span class="metadata-value">${new Date(announcement.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                    </div>
                    ${announcement.expiresAt ? `
                    <div class="metadata-item">
                        <span class="metadata-label">Expires:</span>
                        <span class="metadata-value">${new Date(announcement.expiresAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                    </div>
                    ` : ''}
                </div>
                
                ${announcement.expiresAt ? `
                <div class="expiry-notice">
                    <p><strong>Note:</strong> This announcement will expire on ${new Date(announcement.expiresAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</p>
                </div>
                ` : ''}
            </div>
            
            <div class="footer">
                <p>This is an automated message from ProfStudy Admin Panel</p>
                <p>Please do not reply to this email</p>
            </div>
        </div>
    </body>
    </html>
  `;

  const textContent = `
ProfStudy Announcement
=====================

Title: ${announcement.title}
Priority: ${priorityLabel}
Target Audience: ${announcement.targetAudience.charAt(0).toUpperCase() + announcement.targetAudience.slice(1)}

Content:
${announcement.content}

Created: ${new Date(announcement.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}
${announcement.expiresAt ? `Expires: ${new Date(announcement.expiresAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}` : ''}

---
This is an automated message from ProfStudy Admin Panel
Please do not reply to this email
  `;

  return {
    subject: `[ProfStudy] ${announcement.title}`,
    html: htmlContent,
    text: textContent
  };
};

/**
 * Send email using mock service (for development)
 * In production, this would integrate with a real email service
 */
export const sendEmailMock = async (to, subject, html, text) => {
  // Mock email sending - in development, just log the email
  console.log('📧 Mock Email Sent:');
  console.log('To:', to);
  console.log('Subject:', subject);
  console.log('HTML Content Length:', html.length);
  console.log('Text Content Length:', text.length);
  
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    success: true,
    messageId: `mock-${Date.now()}`,
    message: 'Email sent successfully (mock mode)'
  };
};

/**
 * Send email using real service (for production)
 */
export const sendEmailReal = async (to, subject, html, text) => {
  try {
    const response = await fetch(EMAIL_SERVICE_CONFIG.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to,
        subject,
        html,
        text,
        from: {
          email: EMAIL_SERVICE_CONFIG.fromEmail,
          name: EMAIL_SERVICE_CONFIG.fromName
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Email service error: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

/**
 * Send announcement email to recipients
 */
export const sendAnnouncementEmail = async (announcement, customRecipients = []) => {
  try {
    // Get recipients based on target audience
    let recipients = [];
    
    if (customRecipients.length > 0) {
      // Use custom recipients if provided
      recipients = customRecipients.map(email => ({ email, name: 'User' }));
    } else {
      // Get recipients from database based on target audience
      const userEmails = await getUserEmailsByAudience(announcement.targetAudience);
      const additionalEmails = await getAdditionalEmails();
      
      recipients = [
        ...userEmails,
        ...additionalEmails.map(email => ({ email, name: 'User' }))
      ];
    }

    if (recipients.length === 0) {
      throw new Error('No recipients found for the specified target audience');
    }

    // Generate email content
    const emailContent = generateEmailContent(announcement);
    
    // Send emails
    const results = [];
    const sendEmail = EMAIL_SERVICE_CONFIG.mockMode ? sendEmailMock : sendEmailReal;
    
    for (const recipient of recipients) {
      try {
        const result = await sendEmail(
          recipient.email,
          emailContent.subject,
          emailContent.html,
          emailContent.text
        );
        
        results.push({
          email: recipient.email,
          name: recipient.name,
          success: true,
          messageId: result.messageId,
          message: result.message
        });
      } catch (error) {
        console.error(`Error sending email to ${recipient.email}:`, error);
        results.push({
          email: recipient.email,
          name: recipient.name,
          success: false,
          error: error.message
        });
      }
    }

    return {
      success: true,
      totalRecipients: recipients.length,
      successfulSends: results.filter(r => r.success).length,
      failedSends: results.filter(r => !r.success).length,
      results
    };
  } catch (error) {
    console.error('Error sending announcement email:', error);
    throw error;
  }
};

/**
 * Test email configuration
 */
export const testEmailConfiguration = async () => {
  try {
    const testAnnouncement = {
      title: 'Test Email Configuration',
      content: 'This is a test email to verify the email configuration is working correctly.',
      priority: 'low',
      targetAudience: 'admins',
      createdAt: new Date(),
      expiresAt: null
    };

    const result = await sendAnnouncementEmail(testAnnouncement, ['admin@profstudy.com']);
    return result;
  } catch (error) {
    console.error('Error testing email configuration:', error);
    throw error;
  }
};
